import { Badge, Button, Card, CardBody, Tabs, type TabItem } from "@omnitok/ui";
import {
  AlertCircle,
  ArrowLeft,
  Database,
  History,
  LayoutDashboard,
  Rocket,
  RotateCcw,
  Save,
  Sparkles,
  Zap,
} from "lucide-react";
import { Spinner } from "@pervasivemind/bi-components";
import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { useBuilderDraft, type UseBuilderDraft } from "@/hooks/useBuilderDraft";
import {
  formatShortcut,
  useKeyboardShortcuts,
  type ShortcutHandler,
} from "@/hooks/useKeyboardShortcuts";
import { useT } from "@/i18n";
import { resolveLocalized, type DisplayLabel } from "@pervasivemind/dashboard-render";
import { AITab } from "@/pages/builder/tabs/AITab";
import { DashboardTab } from "@/pages/builder/tabs/DashboardTab";
import { DataTab } from "@/pages/builder/tabs/DataTab";
import { HistoryPanel } from "@/pages/builder/HistoryPanel";
import { PublishModal } from "@/pages/builder/PublishModal";
import { BrokenViewsDialog } from "@/pages/builder/BrokenViewsWarning";
import { cn } from "@/lib/utils";

// Iter 27 follow-up — Preview button + modal removed entirely. The
// Dashboard tab's left pane (``RealDashboardCanvas`` consuming
// ``DashboardSections`` / ``PreviewFilterBar`` directly from
// PreviewModal.tsx) already renders the same content at full
// fidelity, so the modal was duplicate surface area. The file stays
// because those sub-components are still imported by the canvas.

type TabId = "dashboard" | "data" | "ai";

const TAB_IDS: TabId[] = ["dashboard", "data", "ai"];

/**
 * The ``?tab=`` value, or Dashboard.
 *
 * A hand-typed or stale value falls back to Dashboard rather than
 * rendering an empty pane. Exported so the fallback is pinned by a test
 * instead of only existing inside a component.
 */
export function resolveEditorTab(raw: string | null): TabId {
  return raw !== null && (TAB_IDS as string[]).includes(raw)
    ? (raw as TabId)
    : "dashboard";
}

// Maestros / Archivos tabs removed (2026-05-18 audit revisit):
// the portal scopes both surfaces at the PROJECT level, never the
// REPORT level. The builder was editing per-report ``master_files_config``
// + ``files_config`` JSONB columns that the portal never consults
// for Maestros, and only consults aggregated-across-drafts (any
// non-empty config) for Archivos. The editor tabs were dead UI.
// Project-level configuration lives in the portal's Settings →
// Projects flow for now; the report-builder skill manages the
// underlying postgres rows directly.
function buildTabs(t: (key: string) => string): TabItem[] {
  return [
    {
      id: "dashboard",
      label: t("tab.dashboard"),
      icon: <LayoutDashboard size={16} />,
    },
    { id: "data", label: t("tab.data"), icon: <Database size={16} /> },
    { id: "ai", label: t("tab.ai"), icon: <Sparkles size={16} /> },
  ];
}

/**
 * Top-level editor page for a single report draft.
 *
 * Page chrome (back link, identity, badges, save/preview/publish toolbar)
 * is rendered locally because Omnitok ``<Layout hideHeader>`` lets each
 * page own its header for tighter integration with tabbed content.
 */
export function ReportBuilderPage(): JSX.Element {
  const { key = "" } = useParams<{ key: string }>();

  // The active tab lives in the URL, not in component state.
  //
  // It used to be ``useState<TabId>("dashboard")``, which meant a reload,
  // a shared link and the browser's back button all landed on Dashboard
  // no matter which tab you were on — and the SQL of a view or a training
  // example is exactly the kind of thing you paste to somebody else. The
  // project hub two screens away already did it this way
  // (``ProjectHubPage`` ``setSearchParams``); this is the same shape, so
  // the two tab surfaces of the same app finally behave alike.
  //
  // ``replace: true`` keeps tab switching out of the history stack: back
  // should leave the editor, not walk the tabs you clicked.
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: TabId = resolveEditorTab(searchParams.get("tab"));
  const setTab = (next: TabId): void => {
    setSearchParams(next === "dashboard" ? {} : { tab: next }, {
      replace: true,
    });
  };
  const [publishOpen, setPublishOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const draft = useBuilderDraft(key);
  const { t, locale } = useT();
  const tabs = buildTabs(t);

  // Keyboard shortcuts — Cmd/Ctrl + S only.
  // 2026-05-18 audit revisit: the previous Cmd+D (duplicate) and
  // Cmd+Backspace (delete) shortcuts mutated the canvas, which is
  // now read-only. Their handlers were removed alongside the
  // matching canvas buttons; structural changes flow through the
  // report-builder skill.
  // Iter 27 follow-up: Cmd+K (open Preview modal) removed too — the
  // Dashboard tab's canvas already renders at full fidelity, so the
  // modal was duplicate surface area.
  const shortcuts: ShortcutHandler[] = [
    {
      key: "s",
      meta: true,
      run: () => {
        if (draft.dirty && !draft.saving) void draft.save();
      },
    },
  ];
  useKeyboardShortcuts(shortcuts, { enabled: draft.status === "ready" });

  if (draft.status === "loading") {
    return (
      <div className="flex h-[70vh] items-center justify-center text-sm text-neutral-500">
        <Spinner size={16} className="mr-2" ariaLabel={t("common.loading")} />
        {t("common.loading")}
      </div>
    );
  }

  if (draft.status === "error") {
    return (
      <div className="mx-auto mt-12 max-w-md px-6">
        <Card variant="bordered" className="border-error/40 bg-error/5">
          <CardBody>
            <div className="flex items-start gap-3 text-sm text-error-dark">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="font-medium">{t("editor.error.loadDraft", { key })}</p>
                {draft.error && (
                  <p className="mt-1 text-xs">{draft.error}</p>
                )}
                <Button
                  variant="danger"
                  ghost
                  size="xs"
                  onClick={() => void draft.reload()}
                  className="mt-3"
                >
                  {t("common.retry")}
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
        <Link
          to="/"
          className="mt-4 inline-flex items-center gap-1 text-sm text-neutral-600 hover:text-neutral-900"
        >
          <ArrowLeft size={14} /> {t("nav.reports")}
        </Link>
      </div>
    );
  }

  // Iter 19: ``display_name`` is DisplayLabel (str | {en, es}). Resolve
  // to a plain string for the page header using the admin's active UI
  // locale (previously hardcoded ES which leaked into EN renders).
  const displayName =
    resolveLocalized(draft.report.display_name as DisplayLabel, locale) ||
    draft.key;

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        draft={draft}
        keyName={draft.key}
        displayName={displayName}
        onPublish={() => setPublishOpen(true)}
        onHistory={() => setHistoryOpen(true)}
      />

      <div className="border-b border-neutral-200 bg-white px-4 sm:px-6">
        <Tabs
          items={tabs}
          activeId={tab}
          onChange={(id: string) => setTab(id as TabId)}
          variant="underline"
          size="sm"
        />
      </div>

      {/* Tab container — must scroll vertically when content overflows.
         ``overflow-hidden`` was clipping the Data + AI tabs (no inner
         scroll). ``DashboardTab`` already owns its own scroll regions
         via the diagram canvas and is unaffected. */}
      <div className="flex-1 overflow-y-auto bg-neutral-50">
        {tab === "dashboard" && <DashboardTab draft={draft} />}
        {tab === "data" && <DataTab draft={draft} />}
        {tab === "ai" && <AITab draft={draft} />}
      </div>

      <PublishModal
        open={publishOpen}
        onClose={() => setPublishOpen(false)}
        draft={draft}
      />
      <BrokenViewsOnSave draft={draft} />
      <HistoryPanel
        draftKey={draft.key}
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        onReverted={() => void draft.reload()}
      />
    </div>
  );
}

/** A save of the PUBLISHED report goes live, and the server answered that
 *  saved AI Home views would stop working (409, nothing saved): the person
 *  keeps editing or saves anyway (2026-10-26). */
function BrokenViewsOnSave({ draft }: { draft: UseBuilderDraft }): JSX.Element | null {
  const { t } = useT();
  if (draft.viewsConflict === null) return null;
  return (
    <BrokenViewsDialog
      conflict={draft.viewsConflict}
      title={t("views.saveTitle")}
      body={t("views.saveBody")}
      keepLabel={t("views.keepEditing")}
      goLabel={t("views.saveAnyway")}
      busy={draft.saving}
      live
      onKeep={draft.dismissViewsConflict}
      onGo={() => void draft.saveConfirmingViews()}
    />
  );
}

// ── Page header ──────────────────────────────────────────

interface PageHeaderProps {
  draft: UseBuilderDraft;
  keyName: string;
  displayName: string;
  onPublish: () => void;
  onHistory: () => void;
}

function PageHeader({
  draft,
  keyName,
  displayName,
  onPublish,
  onHistory,
}: PageHeaderProps): JSX.Element {
  const { t } = useT();
  const handleUnpublish = (): void => {
    if (window.confirm(t("publish.unpublishConfirm"))) {
      void draft.unpublish();
    }
  };

  return (
    <div className="flex items-center justify-between gap-3 border-b border-neutral-200 bg-white px-4 py-3 sm:px-6">
      <div className="min-w-0 flex-1">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-neutral-900"
        >
          <ArrowLeft size={12} /> {t("nav.reports")}
        </Link>
        {/* ``min-w-0`` on the inner flex is required for the h1's
            ``truncate`` to fire under a flex parent — without it, the
            title grows past the column and pushes the "Publicado" badge
            offscreen (B3 from the 2026-05-18 audit). ``shrink-0`` on the
            badge keeps it pinned next to the title rather than wrapping
            below or being clipped first. */}
        <div className="mt-0.5 flex min-w-0 items-center gap-2">
          <h1 className="min-w-0 truncate font-display text-lg font-extrabold leading-tight tracking-[-0.3px] text-neutral-800">
            {displayName}
          </h1>
          {draft.isPublished && (
            <Badge variant="success" size="sm" pill className="shrink-0">
              {t("editor.published")}
            </Badge>
          )}
        </div>
        <p className="font-mono text-xs text-neutral-500">{keyName}</p>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {draft.saveError && (
          <span className="text-xs text-error-dark" title={draft.saveError}>
            {t("editor.saveFailed")}
          </span>
        )}
        {draft.unpublishError && (
          <span className="text-xs text-error-dark" title={draft.unpublishError}>
            {t("editor.unpublishFailed")}
          </span>
        )}

        <label
          className="hidden cursor-pointer items-center gap-1.5 rounded-md border border-neutral-200 px-2 py-1 text-xs text-neutral-600 hover:bg-neutral-50 sm:inline-flex"
          title={t("editor.autosave")}
        >
          <input
            type="checkbox"
            checked={draft.autoSave}
            onChange={(e) => draft.setAutoSave(e.target.checked)}
            className="h-3.5 w-3.5 rounded border-neutral-300 text-primary focus:ring-primary"
          />
          <Zap size={14} />
          {t("editor.autosave")}
        </label>

        <span
          className={cn(
            "hidden text-xs sm:inline",
            draft.dirty ? "text-warning-dark" : "text-neutral-500",
          )}
        >
          {draft.dirty ? t("editor.dirty") : t("editor.saved")}
        </span>

        <Button
          ghost
          size="sm"
          onClick={onHistory}
          leftIcon={<History size={14} />}
          title={t("history.title")}
        >
          <span className="hidden sm:inline">{t("history.title")}</span>
        </Button>

        <Button
          variant="secondary"
          size="sm"
          onClick={() => void draft.save()}
          disabled={!draft.dirty || draft.saving}
          loading={draft.saving}
          leftIcon={!draft.saving ? <Save size={14} /> : undefined}
          title={`${t("editor.save")} (${formatShortcut("S")})`}
        >
          <span className="hidden sm:inline">{t("editor.save")}</span>
        </Button>

        {draft.isPublished ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={handleUnpublish}
            disabled={draft.publishing}
            loading={draft.publishing}
            leftIcon={!draft.publishing ? <RotateCcw size={14} /> : undefined}
          >
            {t("editor.unpublish")}
          </Button>
        ) : (
          <Button
            variant="primary"
            size="sm"
            onClick={onPublish}
            disabled={draft.publishing}
            loading={draft.publishing}
            leftIcon={!draft.publishing ? <Rocket size={14} /> : undefined}
          >
            {t("editor.publish")}
          </Button>
        )}
      </div>
    </div>
  );
}
